import { useState, useEffect } from 'react'
import api from '../../../services/api'
import { Save, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

export default function GeneralSettings() {
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [formData, setFormData] = useState({
        general: {
            heureDebut: '08:00',
            heureFin: '18:00',
            intervalle: 15,
            allowPastAppointments: false,
            phoneMask: '099 999 99 99'
        },
        smtp: {
            email: '',
            password: '',
            host: '',
            port: 587,
            security: 'AUTO'
        },
        map: {
            provider: 'OPENSTREET_MAP',
            apiKey: ''
        }
    })

    useEffect(() => {
        fetchSettings()
    }, [])

    const fetchSettings = async () => {
        setLoading(true)
        try {
            const res = await api.get('/settings/general')
            // merge with defaults to avoid nulls
            if (res.data.data.settings) {
                const s = res.data.data.settings
                setFormData(prev => ({
                    general: { ...prev.general, ...s.general },
                    smtp: { ...prev.smtp, ...s.smtp },
                    map: { ...prev.map, ...s.map }
                }))
            }
        } catch (err) {
            toast.error('Erreur lors du chargement des paramètres')
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSaving(true)
        try {
            await api.put('/settings/general', formData)
            toast.success('Paramètres enregistrés')
        } catch (err) {
            toast.error('Erreur lors de l\'enregistrement')
        } finally {
            setSaving(false)
        }
    }

    if (loading) return <div className="p-5 text-center"><Loader2 className="animate-spin inline mr-2" /> Chargement...</div>

    return (
        <form onSubmit={handleSubmit}>
            {/* Section A: Heures / Intervalle */}
            <div className="settings-section">
                <h3 className="section-title">Général (Horaires Rendez-vous)</h3>
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Heure Début</label>
                        <input
                            type="time"
                            className="form-input"
                            value={formData.general.heureDebut}
                            onChange={(e) => handleChange('general', 'heureDebut', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Heure Fin</label>
                        <input
                            type="time"
                            className="form-input"
                            value={formData.general.heureFin}
                            onChange={(e) => handleChange('general', 'heureFin', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Intervalle (min)</label>
                        <select
                            className="form-select"
                            value={formData.general.intervalle}
                            onChange={(e) => handleChange('general', 'intervalle', Number(e.target.value))}
                        >
                            <option value={5}>5 min</option>
                            <option value={10}>10 min</option>
                            <option value={15}>15 min</option>
                            <option value={20}>20 min</option>
                            <option value={30}>30 min</option>
                        </select>
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group" style={{ flex: '0 0 auto', marginRight: '2rem' }}>
                        <label className="form-label">Dates antérieures</label>
                        <div className="flex items-center mt-2">
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={formData.general.allowPastAppointments || false}
                                    onChange={(e) => handleChange('general', 'allowPastAppointments', e.target.checked)}
                                />
                                <span className="slider round"></span>
                            </label>
                            <span className="ml-2 text-sm text-gray-600">
                                {formData.general.allowPastAppointments ? 'Autorisé' : 'Interdit'}
                            </span>
                        </div>
                        <p className="text-xs text-muted mt-1">Permettre la saisie de rendez-vous pour une date antérieure</p>
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Masque Téléphone (Format: 09 = Chiffre)</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.general.phoneMask || ''}
                            onChange={(e) => handleChange('general', 'phoneMask', e.target.value)}
                            placeholder="ex: 099 999 99 99"
                        />
                    </div>
                </div>
            </div>

            {/* Section B: SMTP */}
            <div className="settings-section">
                <h3 className="section-title">Configuration SMTP</h3>
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Email (From)</label>
                        <input
                            type="email"
                            className="form-input"
                            value={formData.smtp.email}
                            onChange={(e) => handleChange('smtp', 'email', e.target.value)}
                            placeholder="ex: no-reply@agenda.com"
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Mot de passe</label>
                        <input
                            type="password"
                            className="form-input"
                            value={formData.smtp.password}
                            onChange={(e) => handleChange('smtp', 'password', e.target.value)}
                            placeholder="********"
                        />
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Hôte (Host)</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.smtp.host}
                            onChange={(e) => handleChange('smtp', 'host', e.target.value)}
                            placeholder="smtp.example.com"
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Port</label>
                        <input
                            type="number"
                            className="form-input"
                            value={formData.smtp.port}
                            onChange={(e) => handleChange('smtp', 'port', Number(e.target.value))}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Sécurité</label>
                        <select
                            className="form-select"
                            value={formData.smtp.security}
                            onChange={(e) => handleChange('smtp', 'security', e.target.value)}
                        >
                            <option value="AUTO">AUTO</option>
                            <option value="TLS">TLS</option>
                            <option value="SSL">SSL</option>
                            <option value="NONE">Aucune</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Section C: MAP */}
            <div className="settings-section">
                <h3 className="section-title">Configuration Carte (Map)</h3>
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Fournisseur</label>
                        <select
                            className="form-select"
                            value={formData.map.provider}
                            onChange={(e) => handleChange('map', 'provider', e.target.value)}
                        >
                            <option value="OPENSTREET_MAP">OpenStreet Map</option>
                            <option value="GOOGLE_MAP">Google Map</option>
                        </select>
                    </div>
                    {formData.map.provider === 'GOOGLE_MAP' && (
                        <div className="form-group">
                            <label className="form-label">Clé API (API Key)</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.map.apiKey}
                                onChange={(e) => handleChange('map', 'apiKey', e.target.value)}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="form-actions mt-4">
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                    Enregistrer
                </button>
            </div>
        </form>
    )
}
